// @react
import React from 'react'
// @types
import type { ReactElement } from 'react'
import type { Theme } from '@material-ui/core/styles'
// @libraries
import { makeStyles, createStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
// @components
import Grid from '@bit/atd.web.grid'
import Button from '@bit/atd.web.button'
import Icon from '@bit/atd.web.icons'
import colors from '@bit/atd.web.colors'
import Container from '@bit/atd.web.container'
import GroupTitle from '@bit/atd.web.header-group-title'
import Link from '@bit/atd.web.header-link'
// @parts
import DesktopLinks from './DesktopLinks'
import MobileLinks from './MobileLinks'
// @constants
import { ExternalRouteNames } from 'constants/routeNames'
// @assets
import logoSmall from './assets/atd-logo-small.svg'
import strings from 'lib/constants/strings'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    footer: {
      position: 'relative',
      marginTop: '2em',
      paddingTop: '32px',
      paddingBottom: '24px',
      backgroundColor: colors.gray200,
      '& ul': {
        margin: 0,
        padding: 0,
        listStyle: 'none',
      },
      [theme.breakpoints.up('sm')]: {
        paddingTop: '56px',
        paddingBottom: '38px',
      },
    },
    border: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '2px',
      background: `linear-gradient(90deg, ${colors.brandYellow} 0%, ${colors.brandOrange} 52.98%, ${colors.brandRed} 98.75%)`,
    },
    logoWrap: {
      display: 'flex',
      maxWidth: '500px',
      marginBottom: '48px',
      alignItems: 'center',
      [theme.breakpoints.down(400)]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
      '&:hover': {
        cursor: 'pointer',
      },
      '& img': {
        flexShrink: 0,
        marginRight: '16px',
      },
      '& p': {
        margin: 0,
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '18px',
        [theme.breakpoints.down(400)]: {
          marginTop: '8px',
        },
      },
    },
    svgWrap: {
      width: '100px',
      marginRight: '24px',
    },
    svg: {
      width: '100%',
    },
    mid: {
      [theme.breakpoints.up('sm')]: {
        flexWrap: 'nowrap',
      },
    },
    socialWrap: {
      [theme.breakpoints.up('sm')]: {
        marginLeft: '40px',
      },
    },
    socialIcons: {
      display: 'flex',
      marginBottom: '24px',
      '& a:not(:last-child)': {
        marginRight: '16px',
      },
    },
    btn: {
      backgroundColor: colors.white,
    },
    copyright: {
      marginTop: '30px',
      '& span': {
        fontSize: '12px',
        fontWeight: 500,
      },
      '& p': {
        marginTop: '9px',
        marginBottom: 0,
        fontSize: '10px',
        maxWidth: '327px',
      },
      '& ul': {
        display: 'flex',
      },
      '& li:not(:last-child)': {
        marginRight: '16px',
      },
      [theme.breakpoints.down('xs')]: {
        alignItems: 'flex-start',
        flexDirection: 'column',
        '& .MuiGrid-item:last-child': {
          order: -1,
          marginBottom: '14px',
        },
      },
    },
  }),
)

export type PropTypes = {
  isLoggedIn: boolean
}

const Footer = ({ isLoggedIn }: PropTypes): ReactElement => {
  /********************************************
   *  HOOKS
   *********************************************/

  const theme = useTheme()
  const classes = useStyles()
  const desktop = useMediaQuery(theme.breakpoints.up('sm'))

  /********************************************
   *  RENDER
   *********************************************/

  return (
    <footer className={classes.footer}>
      <span className={classes.border} />
      <Container>
        <div
          className={classes.logoWrap}
          onClick={() => {
            window.location.replace(ExternalRouteNames.ATD_HOME)
          }}
        >
          <div className={classes.svgWrap}>
            <img className={classes.svg} src={logoSmall} alt="ATD" />
          </div>
          <p>{strings.MISSION.DESCRIPTION}</p>
        </div>
        <Grid container className={classes.mid}>
          <Grid item xs={12} sm="auto">
            {desktop && <DesktopLinks isLoggedIn={isLoggedIn} />}
            {!desktop && <MobileLinks isLoggedIn={isLoggedIn} />}
          </Grid>
          <Grid item xs={12} sm="auto" className={classes.socialWrap}>
            <GroupTitle>Stay Connected</GroupTitle>
            <div className={classes.socialIcons}>
              <Link href={ExternalRouteNames.SOCIAL_FACEBOOK}>
                <Icon glyph="facebook" />
              </Link>
              <Link href={ExternalRouteNames.SOCIAL_TWITTER}>
                <Icon glyph="twitter" />
              </Link>
              <Link href={ExternalRouteNames.SOCIAL_LINKEDIN}>
                <Icon glyph="linkedin" />
              </Link>
              <Link href={ExternalRouteNames.SOCIAL_PINTEREST}>
                <Icon glyph="pinterest" />
              </Link>
              <Link href={ExternalRouteNames.SOCIAL_INSTAGRAM}>
                <Icon glyph="instagram" />
              </Link>
            </div>
            {!isLoggedIn && (
              <Button
                variant="secondary"
                href={ExternalRouteNames.CHECKOUT_MEMBERSHIP}
                className={classes.btn}
              >
                Become A Member
              </Button>
            )}
          </Grid>
        </Grid>

        <Grid
          container
          alignItems="center"
          justify="space-between"
          className={classes.copyright}
        >
          <Grid item>
            <span>© 2021 ATD | All Rights Reserved</span>
            <p>
              ASTD changed its name to ATD to meet the growing needs of a
              dynamic, global profession.
            </p>
          </Grid>
          <Grid item>
            <ul>
              <li>
                <Link href={ExternalRouteNames.ATD_TOS}>Terms of Use</Link>
              </li>
              <li>
                <Link href={ExternalRouteNames.ATD_TOS_PRIVACY}>
                  Privacy Notice
                </Link>
              </li>
              <li>
                <Link href={ExternalRouteNames.ATD_COOKIE_POLICY}>
                  Cookies Policy
                </Link>
              </li>
            </ul>
          </Grid>
        </Grid>
      </Container>
    </footer>
  )
}

export default Footer
